import { IcoChevronRight, IcoUser, IcoUsers } from "../icons";

type DirectReport = {
  id: string;
  name: string | null;
};

type UserContextCardProps = {
  /** User's name */
  userName: string;
  /** User's role in the organization */
  userRole?: string | null;
  /** User's job title (optional) */
  userTitle?: string | null;
  /** User's profile picture URL */
  userProfilePictureUrl?: string | null;
  /** Manager's name (optional) */
  managerName?: string | null;
  /** Direct reports */
  directReports?: DirectReport[];
  /** Organization ID for navigation */
  orgId?: string;
  /** Current user's workspace member ID (for profile link) */
  memberId?: string;
};

/**
 * "About Me" sidebar card showing user info, role, manager, and direct reports.
 * Provides org structure context.
 */
export function UserContextCard({
  userName,
  userRole,
  userTitle,
  userProfilePictureUrl,
  managerName,
  directReports = [],
  orgId,
  memberId,
}: UserContextCardProps) {
  const hasDirectReports = directReports.length > 0;
  const displayRole = userRole
    ? userRole.charAt(0).toUpperCase() + userRole.slice(1)
    : null;

  return (
    <div class="rounded-xl border border-border bg-surface overflow-hidden">
      {/* Brand accent bar */}
      <div class="h-1 bg-[var(--brand-500)]" />

      <div class="p-4">
        {/* User Info */}
        <div class="flex items-start gap-3 mb-4">
          <div class="size-12 rounded-full bg-surface-muted flex items-center justify-center overflow-hidden flex-shrink-0">
            {userProfilePictureUrl ? (
              <img
                src={userProfilePictureUrl}
                alt={userName}
                class="size-full object-cover"
              />
            ) : (
              <IcoUser class="size-6 text-text-muted" />
            )}
          </div>
          <div class="min-w-0 flex-1 pt-0.5">
            <p class="font-semibold text-text truncate leading-snug">
              {orgId && memberId ? (
                <a
                  href={`/orgs/${orgId}/members/${memberId}/profile`}
                  class="hover:underline"
                >
                  {userName}
                </a>
              ) : (
                userName
              )}
            </p>
            {(displayRole || userTitle) && (
              <p class="text-xs text-text-muted truncate mt-0.5">
                {userTitle || displayRole}
              </p>
            )}
          </div>
        </div>

        {/* Org Structure */}
        <div class="space-y-2">
          {/* Manager */}
          {managerName && (
            <div class="flex items-center gap-2.5 p-2.5 rounded-lg bg-surface-muted/50">
              <div class="size-7 rounded-full bg-surface flex items-center justify-center flex-shrink-0">
                <IcoUser class="size-3.5 text-text-subtle" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-[10px] text-text-subtle uppercase tracking-wider font-semibold leading-none mb-1">
                  Reports to
                </p>
                <p class="text-sm font-medium text-text truncate leading-snug">
                  {managerName}
                </p>
              </div>
            </div>
          )}

          {/* Direct Reports */}
          {hasDirectReports && (
            <div class="p-2.5 rounded-lg bg-surface-muted/50">
              <div class="flex items-center gap-2.5">
                <div class="size-7 rounded-full bg-surface flex items-center justify-center flex-shrink-0">
                  <IcoUsers class="size-3.5 text-text-subtle" />
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-[10px] text-text-subtle uppercase tracking-wider font-semibold leading-none mb-1">
                    Direct Reports
                  </p>
                  <p class="text-sm font-medium text-text leading-snug">
                    {directReports.length}{" "}
                    {directReports.length === 1 ? "person" : "people"}
                  </p>
                </div>
              </div>

              {/* Report names */}
              <div class="flex flex-wrap gap-1 mt-2.5 pl-[2.375rem]">
                {directReports.slice(0, 4).map((report) => (
                  <span
                    key={report.id}
                    class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-surface text-text-secondary"
                  >
                    {report.name || "Unknown"}
                  </span>
                ))}
                {directReports.length > 4 && (
                  <span class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-surface text-text-muted">
                    +{directReports.length - 4} more
                  </span>
                )}
              </div>

              {/* Link to team page */}
              {orgId && (
                <a
                  href={`/orgs/${orgId}/team`}
                  class="group flex items-center gap-0.5 mt-2 pl-[2.375rem] text-xs font-medium text-[var(--brand-600)] hover:text-[var(--brand-700)]"
                >
                  View team
                  <IcoChevronRight class="size-3 transition-transform duration-150 group-hover:translate-x-0.5" />
                </a>
              )}
            </div>
          )}

          {/* Empty state when no manager and no reports */}
          {!managerName && !hasDirectReports && (
            <p class="text-sm text-text-muted text-center py-2">
              No org structure set up yet
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
